import { Route, RouteConfig } from "vue-router";

export const patientFormsRoutes: Array<RouteConfig> = [
  {
    path: "/patient-forms-selector",
    name: "patientFormsSelector",
    meta: {
      authRequired: true,
      userTypes: ["employee"],
      breadcrumb(route: Route) {
        return [
          {
            text: "Compilazione Schede",
          },
        ];
      },
      beforeResolve(routeTo: Route, routeFrom: Route, next: Function) {
        const store = require("@/store").default;
        const user = store.getters.stateUser;
        
        if (!user) {
          return next({ name: "login" });
        }

        let hasInfermiereAccess = false;
        
        if (user.employee && user.employee.user_group) {
          const groupName = user.employee.user_group.name;
          hasInfermiereAccess = groupName === "infermiere" || groupName === "Infermiere";
        }
        
        if (!hasInfermiereAccess && user.user_groups) {
          hasInfermiereAccess = user.user_groups.some((group: any) => 
            group.name === "infermiere" || group.name === "Infermiere"
          );
        }

        if (!hasInfermiereAccess) {
          return next({ name: "dashboardusers" });
        }

        next();
      },
    },
    component: () =>
      import(
        /* webpackChunkName: "patient-forms-selector" */ "../../../views/PatientFormsSelectorView.vue"
      ),
  },
  {
    path: "/patient-forms/:patientId",
    name: "patientFormCompilation",
    meta: {
      authRequired: true,
      userTypes: ["employee"], // Restrict to employee type
      breadcrumb(route: Route) {
        return [
          {
            text: "Compilazione Schede",
            to: { name: "patientFormsSelector" },
          },
          {
            text: "Compila Schede Paziente",
          },
        ];
      },
      beforeResolve(routeTo: Route, routeFrom: Route, next: Function) {
        // Additional role-based check for "infermiere" group
        const store = require("@/store").default;
        const user = store.getters.stateUser;
        
        if (!user) {
          return next({ name: "login" });
        }

        // Check if user is in "infermiere" group
        let hasInfermiereAccess = false;
        
        // Check employee.user_group (singular)
        if (user.employee && user.employee.user_group) {
          const groupName = user.employee.user_group.name;
          hasInfermiereAccess = groupName === "infermiere" || groupName === "Infermiere";
        }
        
        // Fallback: Check user_groups array (for future compatibility)
        if (!hasInfermiereAccess && user.user_groups) {
          hasInfermiereAccess = user.user_groups.some((group: any) => 
            group.name === "infermiere" || group.name === "Infermiere"
          );
        }

        if (!hasInfermiereAccess) {
          return next({ name: "dashboardusers" });
        }

        // Redirect to SinglePatientView with compilazione-schede tab
        const patientId = routeTo.params.patientId;
        return next({
          name: "singlePatient",
          params: { id: patientId },
          query: { tab: "compilazione-schede" }
        });
      },
    },
    // No component needed since we redirect
  },
  {
    path: "/patient/:patientId/scheda-n2",
    name: "schedaN2",
    meta: {
      authRequired: true,
      userTypes: ["employee"],
      breadcrumb(route: Route) {
        const patientName = route.query.patientName || 'Paziente';
        const patientId = route.params.patientId;
        
        return [
          {
            text: "Patients",
            to: { name: "patients" },
          },
          {
            text: patientName,
            to: {
              name: "singlePatient",
              params: { id: patientId },
              query: { patientName },
            },
          },
          {
            text: "Scheda N2",
          },
        ];
      },
      beforeResolve(routeTo: Route, routeFrom: Route, next: Function) {
        const store = require("@/store").default;
        const user = store.getters.stateUser;

        if (!user) {
          return next({ name: "login" });
        }

        // Check if user has appropriate permissions (infermiere employee or nutritionist)
        let hasAccess = false;

        // Check if user is a nutritionist
        if (user.type === 'nutritionist') {
          hasAccess = true;
        }
        // Check if user is an employee with infermiere group
        else if (user.employee && user.employee.user_group) {
          const groupName = user.employee.user_group.name.toLowerCase();
          hasAccess = groupName === "infermiere";
        }

        // Fallback: Check user_groups array for infermiere
        if (!hasAccess && user.user_groups) {
          hasAccess = user.user_groups.some((group: any) => {
            const groupName = group.name.toLowerCase();
            return groupName === "infermiere";
          });
        }

        if (!hasAccess) {
          return next({ name: "dashboardusers" });
        }

        next();
      },
    },
    component: () =>
      import(
        /* webpackChunkName: "scheda-n2" */ "../../../views/SchedaN2View.vue"
      ),
  },
  {
    path: "/patient/:patientId/scheda-n3",
    name: "schedaN3",
    meta: {
      authRequired: true,
      userTypes: ["employee"],
      breadcrumb(route: Route) {
        const patientName = route.query.patientName || 'Paziente';
        const patientId = route.params.patientId;
        
        return [
          {
            text: "Patients",
            to: { name: "patients" },
          },
          {
            text: patientName,
            to: {
              name: "singlePatient",
              params: { id: patientId },
              query: { patientName },
            },
          },
          {
            text: "Scheda N3",
          },
        ];
      },
      beforeResolve(routeTo: Route, routeFrom: Route, next: Function) {
        const store = require("@/store").default;
        const user = store.getters.stateUser;

        if (!user) {
          return next({ name: "login" });
        }

        // Check if user has appropriate permissions (only nutritionist for N3)
        // Nutritionist users have type === 'nutritionist' (not employees)
        const hasAccess = user.type === 'nutritionist';

        if (!hasAccess) {
          return next({ name: "dashboardusers" });
        }

        next();
      },
    },
    component: () =>
      import(
        /* webpackChunkName: "scheda-n3" */ "../../../views/SchedaN3View.vue"
      ),
  },
];